import React from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import Overlay from 'react-bootstrap/Overlay';
import deleteIcon from 'src/assets/icons/delete.svg';
import editIcon from 'src/assets/icons/editIcon.svg';
import './EventDetailTooltip.scss';
import EventTooltipAddressContainer from './EventTooltipAddressContainer';
import EventTooltipDetail from './EventTooltipDetail';
import { Appointment } from '../calendar/calendarTypes';
import { sanitiseHTML, stringToObject } from 'src/legacy/utils/helpers';
import { AppointmentType, EventType } from '../../constants/EventEnums';
import { MeetingType } from '../../constants/MeetingTypeEnums';
import { Typography } from 'src/latest/src/components';
import { InfoTooltip } from 'src/latest/src/components/InfoTooltip';
import autoGeneratedIcon from 'src/assets/icons/tooltip-logo.png';

interface Prop {
  clientName?: string;
  finalDateTime: string;
  handleDelete: any;
  isCalendarTooltip: boolean;
  isAppointmentTooltip: boolean;
  handleEdit?: any;
  isTooltipVisible?: boolean;
  tooltipTarget: any;
  closeTooltip?: any;
  showTooltip?: any;
  data: Appointment;
  isGoogleSynced?: boolean;
}

const EventDetailTooltip: React.FC<Prop> = ({
  clientName,
  finalDateTime,
  handleDelete,
  isCalendarTooltip,
  isAppointmentTooltip,
  handleEdit,
  isTooltipVisible,
  tooltipTarget,
  closeTooltip,
  showTooltip,
  data,
  isGoogleSynced = false,
}) => {
  const isOpeningEvent =
    isCalendarTooltip && data?.eventType === EventType.Opening;

  const showOpeningLocation = [
    MeetingType.InPerson,
    MeetingType.OnlineOrInPerson,
  ].includes(data?.meeting_pref?.toUpperCase());

  const openingOfficeLocation = showOpeningLocation
    ? data?.therapistDetails?.office_location
    : undefined;

  const hasOpeningOfficeAddress =
    isOpeningEvent &&
    typeof openingOfficeLocation === 'string' &&
    openingOfficeLocation.includes('addressInfo');

  const appointmentLocation: any = hasOpeningOfficeAddress
    ? stringToObject(openingOfficeLocation)
    : isOpeningEvent
    ? openingOfficeLocation
    : (isCalendarTooltip && data?.eventType === EventType.Appointment) ||
      isAppointmentTooltip
    ? stringToObject(data?.office_location)
    : data?.office_location;

  const handleClientNameClick = () => {
    const clientId = data?.note?.ClientId || data?.client_id;

    if (clientId) {
      window.open(`/profile/${clientId}#profile`, '_blank');
    }
  };

  const getServiceTypeDisplay = (serviceTypes?: string) => {
    if (!serviceTypes) return '';
    const services = serviceTypes.split(',').map(s => s.trim());

    return services.length === 1 ? services[0] : 'Multiservice';
  };

  const isAppointmentEvent =
    isAppointmentTooltip || data?.eventType === EventType.Appointment;
  const isOnlineAppointment =
    isAppointmentEvent && data?.appointment_type === AppointmentType.Telehealth;

  const getMeetingPrefDisplay = () => {
    if (isOnlineAppointment) return 'Online';
    if (
      isAppointmentEvent &&
      data?.appointment_type === AppointmentType.InPerson
    )
      return 'In person';

    return data?.meeting_pref;
  };

  return (
    <>
      <Overlay
        rootClose={true}
        target={tooltipTarget?.current}
        show={isCalendarTooltip ? showTooltip : isTooltipVisible}
        placement={isCalendarTooltip ? 'bottom' : 'right'}
        onHide={closeTooltip}
        popperConfig={{
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ],
        }}
      >
        <Tooltip
          id="event-tooltip"
          placement="bottom"
          className="appointment-tooltip"
        >
          <div className={`tooltip-content ${isCalendarTooltip ? '' : 'mt-5'}`}>
            <div className="tooltip-header">
              <Typography
                weight="bold"
                variant="size-18"
                className="event-title clickable-client-name"
                style={{ cursor: clientName ? 'pointer' : 'default' }}
              >
                {clientName}
              </Typography>
              <Typography variant="size-16" className="time-string">
                {finalDateTime}
              </Typography>
              {isCalendarTooltip && (
                <Typography variant="size-12" color="color-28" className="mt-2">
                  {data?.event_description}
                </Typography>
              )}
            </div>
            {data.eventType !== EventType.GoogleEvent && (
              <div className="tooltip-body">
                <EventTooltipDetail
                  keyName={data?.note?.['Name of Client'] || data?.client_name}
                  valueName={'Patient'}
                  onClick={handleClientNameClick}
                />
                <EventTooltipDetail
                  keyName={
                    data?.therapistDetails?.therapist_name ||
                    data?.therapist_name
                  }
                  valueName={'Provider'}
                />
                <EventTooltipDetail
                  keyName={data?.scheduler_name}
                  valueName={'Scheduler'}
                />
              </div>
            )}
            <div className="tooltip-body">
              {data.eventType === EventType.GoogleEvent ? (
                <div className="g-event-desc mb-2">
                  {sanitiseHTML(
                    data?.metadata?.google_description
                      ? data?.metadata?.google_description
                      : '',
                  )}
                </div>
              ) : (
                <>
                  {/* <br />
                  <Typography variant="size-14">
                    <div>
                      <Typography
                        variant="size-14"
                        weight="semi-bold"
                        color="color-2"
                      >
                        {`${
                          isCalendarTooltip ? data?.eventType : data.slot_type
                        }`}
                      </Typography>
                    </div>
                  </Typography> */}
                  <Typography variant="size-14">
                    <div className="d-flex">
                      <div>
                        <Typography
                          className="service-type"
                          weight="semi-bold"
                          color="color-2"
                          variant="size-14"
                        >
                          {getServiceTypeDisplay(
                            data?.service_type || data?.service_types,
                          )}
                        </Typography>
                      </div>
                      <div className="ms-2">
                        <Typography
                          weight="semi-bold"
                          color="color-2"
                          variant="size-14"
                        >
                          {`•  ${getMeetingPrefDisplay()}`}
                        </Typography>
                      </div>
                    </div>
                  </Typography>
                  {!isOnlineAppointment &&
                    appointmentLocation &&
                    appointmentLocation !== '' &&
                    Object.entries(appointmentLocation).length > 0 && (
                      <div className="address-container">
                        {((isCalendarTooltip &&
                          data?.eventType === EventType.Appointment) ||
                          isAppointmentTooltip ||
                          hasOpeningOfficeAddress) && (
                          <div className="appointmentLocation-in-person-ofc">
                            <EventTooltipAddressContainer
                              appointmentLocation={appointmentLocation}
                            />
                          </div>
                        )}

                        {isCalendarTooltip &&
                          data?.eventType !== EventType.Appointment &&
                          !hasOpeningOfficeAddress && (
                            <Typography
                              variant="size-14"
                              weight="semi-bold"
                              color="color-2"
                            >
                              {appointmentLocation}
                            </Typography>
                          )}
                      </div>
                    )}
                </>
              )}
            </div>
            {data.eventType === EventType.Opening && isGoogleSynced ? (
              <div className="tooltip-footer auto-generated-footer mt-3">
                <InfoTooltip
                  className="auto-generated-info"
                  description={
                    <>
                      Auto-created by i2i based on availability in your Google
                      Calendar and your set working hours. To remove, block this
                      time in Google Calendar or update your i2i Availability
                      Settings.
                    </>
                  }
                  placement="bottom"
                />
                <Typography
                  variant="size-10"
                  color="color-28"
                  className="auto-generated-text"
                >
                  Auto-generated by
                  <>
                    <img
                      src={autoGeneratedIcon}
                      className="ms-2"
                      alt="autoGeneratedIcon"
                    />
                  </>
                </Typography>
              </div>
            ) : (
              data.eventType !== EventType.GoogleEvent &&
              data.eventType !== EventType.Verification && (
                <div className="tooltip-footer">
                  {isCalendarTooltip && (
                    <button onClick={handleEdit} className="edit-button">
                      <img src={editIcon} alt="editIcon" />
                    </button>
                  )}
                  <button onClick={handleDelete} className="delete-button">
                    <img src={deleteIcon} alt="deleteIcon" />
                  </button>
                </div>
              )
            )}
          </div>
        </Tooltip>
      </Overlay>
    </>
  );
};

export default EventDetailTooltip;
